import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { isDeepEqual, mapValues } from 'remeda';

import { ITab } from '../ui/components/tabs/types';

type RecursiveTrim<T> = T extends string
  ? string
  : T extends object
    ? { [K in keyof T]: RecursiveTrim<T[K]> }
    : T;

/**
 * Приенить trim ко всем строкам в объекте
 * @param obj Объект для модифицирования
 * @returns Новый объект
 */
export function trimStringsInObject<T extends object>(
  obj: T
): RecursiveTrim<T> {
  return mapValues(obj, value => {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return trimStringsInObject(value);
    }
    return value;
  }) as RecursiveTrim<T>;
}

/**
 * Получение изменний между объектами
 * @param original Оригинальный объект
 * @param draft Объект для сравнения
 */
export function getChangedFields<T extends object>(
  original: T,
  draft: T
): Partial<T> {
  const changes: Partial<T> = {};

  for (const key in draft) {
    if (!isDeepEqual(draft[key], original[key])) {
      changes[key] = draft[key];
    }
  }

  return changes;
}

/**
 * Переключение на следующий таб
 * @param tabs - табы
 * @param activeTab - активный таб
 * @param updateActiveTab - установить активный таб
 */
export const switchNextTab = <T extends string>(
  tabs: Record<T, ITab>,
  activeTab: T | null | undefined,
  updateActiveTab: (newTab: T) => void
) => {
  const tabKeys = Object.keys(tabs) as T[];
  const currentIndex = tabKeys.indexOf(activeTab || tabKeys[0]);

  if (currentIndex === -1 || currentIndex === tabKeys.length - 1) {
    return;
  }

  const nextTabKey = tabKeys[currentIndex + 1];
  updateActiveTab(nextTabKey);
};

/**
 * Обрезка текста до передаваемой длины символов с добавлением многоточия
 * @param text - текст
 * @param maxLength - максимальная длина
 */
export const truncateText = (text: string, maxLength: number): string =>
  `${text.substring(0, maxLength)}${text.length > maxLength ? '...' : ''}`;

/**
 * Преобразовании переданных sx для возможности объединения с другими
 * @param sx Стили
 */
export const passedSx = (sx: SxProps<Theme>) => (Array.isArray(sx) ? sx : [sx]);
